/**
 * Prioritized Intelligence Requirements.
 *
 * The screen that inverts the usual order. A SOC states what it needs to know, in ATT&CK terms;
 * those requirements then select feeds and justify rules, and their coverage is measured. A
 * requirement with no detection behind it is a gap the SOC chose — this screen makes sure it
 * knows it chose it.
 */

import { useState } from 'react'
import { api, type Pir, type RuleDraftResult } from '../api'
import { useSession } from '../App'
import {
  Async,
  Bar,
  Card,
  Chip,
  Empty,
  Modal,
  Note,
  fmtRelative,
  useAsync,
  useToast,
} from '../components/ui'

const PRIORITIES = ['critical', 'high', 'medium', 'low'] as const

const FLOOR: Record<string, string> = {
  critical: 'high',
  high: 'medium',
  medium: 'low',
  low: 'info',
}

export default function Requirements() {
  const { can, me } = useSession()
  const toast = useToast()
  const pirs = useAsync<Pir[]>(() => api.get<Pir[]>('/intel/requirements'), [me.tenant])
  const [editing, setEditing] = useState<Pir | null>(null)
  const [creating, setCreating] = useState(false)
  const [drafting, setDrafting] = useState<Pir | null>(null)

  function remove(p: Pir) {
    if (!window.confirm(`Delete "${p.title}"? Rules written for it stay, but stop counting toward any requirement.`)) return
    api
      .del(`/intel/requirements/${p.id}`)
      .then(() => {
        toast('ok', 'Requirement deleted')
        pirs.reload()
      })
      .catch((e: Error) => toast('error', e.message))
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Intelligence requirements</h1>
          <div className="sub">
            What this SOC has decided it needs to know. Raising a requirement's priority raises the
            severity floor of every detection written against it, without editing them one by one —
            which is what makes a requirement operational rather than a document.
          </div>
        </div>
        <div className="actions">
          {can('configure_rules') && (
            <button className="primary" onClick={() => setCreating(true)}>
              New requirement
            </button>
          )}
        </div>
      </div>

      <Async
        state={pirs}
        empty={
          <Card>
            <Empty title="No requirements defined">
              Without stated requirements, coverage has nothing to be measured against and every
              feed looks equally worth ingesting.
            </Empty>
          </Card>
        }
      >
        {(list) => (
          <div className="stack">
            {list.map((p) => (
              <Card
                key={p.id}
                title={
                  <span className="row" style={{ gap: 8 }}>
                    <Chip tone={p.priority === 'critical' ? 'critical' : p.priority === 'high' ? 'high' : p.priority === 'medium' ? 'medium' : 'low'}>
                      {p.priority}
                    </Chip>
                    {p.title}
                    {!p.active && <Chip tone="neutral">inactive</Chip>}
                  </span>
                }
                subtitle={p.id}
                actions={
                  can('configure_rules') ? (
                    <>
                      <button className="sm" onClick={() => setDrafting(p)}>
                        Draft a rule
                      </button>
                      <button className="sm" onClick={() => setEditing(p)}>
                        Edit
                      </button>
                      <button className="sm danger" onClick={() => remove(p)}>
                        Delete
                      </button>
                    </>
                  ) : null
                }
              >
                <p style={{ fontStyle: 'italic' }} className="muted">
                  “{p.question}”
                </p>

                <div className="row" style={{ marginBottom: 10, alignItems: 'center' }}>
                  <div style={{ flex: 1, maxWidth: 320 }}>
                    <Bar
                      pct={p.coverage_pct}
                      tone={p.coverage_pct >= 80 ? 'ok' : p.coverage_pct >= 40 ? 'warn' : 'danger'}
                    />
                  </div>
                  <span className="tnum small">
                    {p.coverage_pct}% covered by {p.rule_ids.length} rule(s)
                  </span>
                </div>

                {p.uncovered.length > 0 && (
                  <Note tone={p.priority === 'critical' ? 'danger' : 'warn'}>
                    No rule detects{' '}
                    {p.uncovered.map((t) => (
                      <Chip key={t.id} tone="outline" title={t.name}>
                        {t.id}
                      </Chip>
                    ))}
                    . Until one does, this requirement is stated but not met.
                  </Note>
                )}

                <dl className="kv" style={{ marginTop: 10 }}>
                  <dt>Techniques</dt>
                  <dd>
                    {p.techniques.map((t) => (
                      <Chip key={t.id} tone="outline" title={`${t.name} · ${t.tactic}`}>
                        {t.id}
                      </Chip>
                    ))}
                  </dd>
                  <dt>Severity floor</dt>
                  <dd>
                    detections raised to at least <Chip tone={FLOOR[p.priority] as never}>{FLOOR[p.priority]}</Chip>
                  </dd>
                  {p.feeds.length > 0 && (
                    <>
                      <dt>Feeds expected</dt>
                      <dd className="small">{p.feeds.join(', ')}</dd>
                    </>
                  )}
                  {p.assets.length > 0 && (
                    <>
                      <dt>Assets</dt>
                      <dd className="small">{p.assets.join(', ')}</dd>
                    </>
                  )}
                  {p.review_at && (
                    <>
                      <dt>Review</dt>
                      <dd className="small">{fmtRelative(p.review_at)}</dd>
                    </>
                  )}
                </dl>
              </Card>
            ))}
          </div>
        )}
      </Async>

      {(editing || creating) && (
        <PirEditor
          pir={editing}
          onClose={() => {
            setEditing(null)
            setCreating(false)
          }}
          onSaved={pirs.reload}
        />
      )}

      {drafting && (
        <DraftPanel
          pir={drafting}
          onClose={() => setDrafting(null)}
          onSaved={pirs.reload}
        />
      )}
    </>
  )
}

function PirEditor({ pir, onClose, onSaved }: { pir: Pir | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [title, setTitle] = useState(pir?.title ?? '')
  const [question, setQuestion] = useState(pir?.question ?? '')
  const [priority, setPriority] = useState<string>(pir?.priority ?? 'high')
  const [techniques, setTechniques] = useState((pir?.techniques ?? []).map((t) => `${t.id}|${t.name}|${t.tactic}`).join('\n'))
  const [feeds, setFeeds] = useState((pir?.feeds ?? []).join(', '))
  const [assets, setAssets] = useState((pir?.assets ?? []).join(', '))
  const [busy, setBusy] = useState(false)

  function save() {
    const parsed = techniques
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [id = '', name = '', tactic = 'execution'] = l.split('|').map((s) => s.trim())
        return { id, name: name || id, tactic }
      })
      .filter((t) => t.id)

    if (parsed.length === 0) {
      toast('error', 'A requirement with no technique cannot be measured against any detection.')
      return
    }

    setBusy(true)
    api
      .put('/intel/requirements', {
        id: pir?.id ?? '',
        title,
        question,
        priority,
        techniques: parsed,
        actors: pir?.actors ?? [],
        assets: assets.split(',').map((s) => s.trim()).filter(Boolean),
        feeds: feeds.split(',').map((s) => s.trim()).filter(Boolean),
        rule_ids: [],
        created_at: pir?.created_at ?? Date.now(),
        created_by: pir?.created_by ?? '',
        active: pir?.active ?? true,
        review_at: pir?.review_at ?? null,
      })
      .then(() => {
        toast('ok', 'Requirement saved and relinked to the rules that cover it')
        onSaved()
        onClose()
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      title={pir ? `Edit ${pir.id}` : 'New intelligence requirement'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy || !title.trim() || !question.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="pir-title">Title</label>
        <input id="pir-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ransomware affiliates targeting our sector" />
      </div>
      <div className="field">
        <label htmlFor="pir-q">The question it must answer</label>
        <textarea
          id="pir-q"
          rows={3}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Are ransomware affiliates active against organizations like ours, and would we see the encryption stage before the damage is done?"
        />
        <div className="hint">
          Write it as a question. A requirement phrased as a topic (“ransomware”) cannot be
          answered, so it cannot be closed.
        </div>
      </div>
      <div className="field">
        <label htmlFor="pir-pri">Priority</label>
        <select id="pir-pri" value={priority} onChange={(e) => setPriority(e.target.value)}>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p} — detections raised to at least {FLOOR[p]}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="pir-tech">Techniques (one per line, <code>id|name|tactic</code>)</label>
        <textarea
          id="pir-tech"
          rows={5}
          value={techniques}
          onChange={(e) => setTechniques(e.target.value)}
          placeholder={'T1486|Data Encrypted for Impact|impact\nT1490|Inhibit System Recovery|impact'}
        />
        <div className="hint">
          Coverage is measured against these. A rule for a sub-technique counts toward a
          parent-level requirement.
        </div>
      </div>
      <div className="grid cols-2">
        <div className="field">
          <label htmlFor="pir-feeds">Feeds expected to satisfy it</label>
          <input id="pir-feeds" value={feeds} onChange={(e) => setFeeds(e.target.value)} placeholder="abusech, cisa_kev" />
        </div>
        <div className="field">
          <label htmlFor="pir-assets">Assets it protects</label>
          <input id="pir-assets" value={assets} onChange={(e) => setAssets(e.target.value)} placeholder="file servers, backup infrastructure" />
        </div>
      </div>
    </Modal>
  )
}

/**
 * The drafting panel.
 *
 * Shows a proposal and the deterministic verdict on it side by side, and never saves anything on
 * its own. The operator reads what the validator said, edits if they want, and saves — and that
 * act is the acceptance the AI boundary requires (ADR-0002). A one-click "accept" that skipped the
 * verdict would turn the boundary into a formality.
 */
function DraftPanel({ pir, onClose, onSaved }: { pir: Pir; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [state, setState] = useState<'idle' | 'drafting' | 'done'>('idle')
  const [result, setResult] = useState<RuleDraftResult | null>(null)
  const [saving, setSaving] = useState(false)

  function draft() {
    setState('drafting')
    api
      .post<RuleDraftResult>(`/intel/requirements/${pir.id}/draft-rule`, {})
      .then((r) => {
        setResult(r)
        setState('done')
      })
      .catch((e: Error) => {
        toast('error', e.message)
        setState('idle')
      })
  }

  function save() {
    if (!result?.rule) return
    setSaving(true)
    api
      .put('/rules', result.rule)
      .then(() => {
        toast('ok', 'Rule saved, disabled. Review it and enable it when you are satisfied.')
        onSaved()
        onClose()
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setSaving(false))
  }

  return (
    <Modal title={`Draft a rule for “${pir.title}”`} onClose={onClose} wide>
      <p className="muted small">
        The model proposes; the validator below checks the proposal against the real condition
        language and the fields your enabled sources actually keep. Nothing is saved until you save
        it, and a saved draft arrives disabled.
      </p>

      {state === 'idle' && (
        <div className="row">
          <button className="primary" onClick={draft}>
            Draft a rule
          </button>
        </div>
      )}

      {state === 'drafting' && <div className="muted small">Drafting…</div>}

      {state === 'done' && result && (
        <div className="stack" style={{ gap: 12 }}>
          {result.refused && (
            <Note tone="warn">
              <strong>No rule is being offered.</strong> {result.refused}
            </Note>
          )}

          {result.draft && (
            <Card title="What the model proposed">
              <dl className="kv">
                <dt>Name</dt>
                <dd>{result.draft.name}</dd>
                <dt>Severity</dt>
                <dd>{result.draft.severity || '—'}</dd>
                <dt>Techniques</dt>
                <dd>
                  {result.draft.techniques.map((t) => (
                    <Chip key={t} tone="outline">
                      {t}
                    </Chip>
                  ))}
                </dd>
                {result.draft.false_positive_notes && (
                  <>
                    <dt>Would fire wrongly when</dt>
                    <dd className="small">{result.draft.false_positive_notes}</dd>
                  </>
                )}
              </dl>
            </Card>
          )}

          {result.unmatched_techniques.length > 0 && (
            <Note tone="warn">
              The draft named{' '}
              {result.unmatched_techniques.map((t) => (
                <Chip key={t} tone="outline">
                  {t}
                </Chip>
              ))}
              , which this requirement does not cover. Either the draft drifted, or the requirement
              should be widened — the platform will not decide that for you.
            </Note>
          )}

          {result.validation.length > 0 && (
            <Note tone="danger">
              <strong>The validator raised {result.validation.length} point(s):</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {result.validation.map((v, i) => (
                  <li key={i} className="small">
                    {v}
                  </li>
                ))}
              </ul>
            </Note>
          )}

          {result.rule && (
            <>
              <Card title="The rule as it would be saved" subtitle="Disabled until you enable it">
                <pre
                  className="mono small"
                  style={{ maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}
                >
                  {JSON.stringify(result.rule, null, 2)}
                </pre>
              </Card>
              <div className="row">
                <button className="primary" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save as a disabled rule'}
                </button>
                <button onClick={draft}>Draft again</button>
              </div>
            </>
          )}

          {!result.rule && <button onClick={draft}>Try again</button>}
        </div>
      )}
    </Modal>
  )
}
